import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import { createUseStyles } from 'react-jss';
import { AlertService, Button, DatePicker, TypeAhead, TextInput, Theme as theme } from '@spoiler-alert/ui-library';
import DeletePostsWidget from '../data-utilities/delete-posts-widget';
import moment from 'moment';
import TitleService from '../../services/title-service';
import { Breadcrumbs } from '../../store';
import { SiteNameTypeAheadQuery } from '../../graphql/queries';
import { EditSite, UpdateAverageGrossMargin } from '../../graphql/mutations';

const useStyles = createUseStyles({
  dataUtilitiesGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  dataUtility: {
    width: '50%',
    minWidth: 500,
    boxSizing: 'border-box',
    padding: [0, 20, 20, 0],
  },
  dataUtilityInner: {
    padding: 15,
    border: `2px ${theme.grey80} solid`,
    borderRadius: 2,
    height: '100%',
    boxShadow: '0 1px 10px rgba(0,0,0,0.3)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  inputField: {
    width: '100%',
    '&:first-child': {
      marginRight: 24,
    },
  },
});

const HelloFreshAdminPage = ({}) => {
  const classes = useStyles();

  useEffect(() => {
    TitleService.setTitles('Hello Fresh Edits');
    Breadcrumbs.set([
      {
        url: '/hello-fresh',
        title: 'Hello Fresh Edits',
      },
    ]);
  }, []);

  const [siteId, setSiteId] = useState('');
  const [averageGrossMargin, setAverageGrossMargin] = useState(0);
  const [day, setDay] = useState();
  const [charities, setCharities] = useState([]);
  const [charityName, setCharityName] = useState();
  const [siteName, setSiteName] = useState();
  const [updateAverageGrossMagin, { loading: grossMarginLoading }] = useMutation(UpdateAverageGrossMargin);
  const [updateSiteFields] = useMutation(EditSite);
  const handleSave = async () => {
    if (!siteId.trim() || !averageGrossMargin || !day) {
      AlertService.alert({
        type: 'warning',
        message: <span>All fields are required to update avgGrossMargin</span>,
        autoDismiss: true,
        dismissDelay: 3000,
      });
      return;
    }
    try {
      const results = await updateAverageGrossMagin({
        variables: {
          siteId: siteId.trim(),
          averageGrossMargin: Number.parseFloat(averageGrossMargin),
          date: moment(day).startOf('day').toDate(),
        },
      });

      if (!results.data.updateAverageGrossMargin.errors || results.data.updateAverageGrossMargin.errors.length === 0) {
        AlertService.alert({
          type: 'success',
          message: <span>Average Gross Margin updated</span>,
          autoDismiss: true,
          dismissDelay: 3000,
        });
        setSiteId('');
        setAverageGrossMargin(0);
        setDay();
      } else {
        const errorMessages = results.data.updateAverageGrossMargin.errors.map((err) => err.message).join(', ');
        AlertService.alert({
          type: 'warning',
          message: <span>Error updating Average Gross Margin: {errorMessages}</span>,
          autoDismiss: true,
          dismissDelay: 3000,
        });
      }
    } catch (err) {
      AlertService.alert({
        type: 'warning',
        message: <span>{err?.message || 'Error updating Average Gross Margin'}</span>,
        autoDismiss: true,
        dismissDelay: 3000,
      });
    }
  };

  const handleDateSelection = (value) => {
    setDay(value);
  };

  const errorMessage = (error) => {
    AlertService.alert({
      type: 'warning',
      autoDismiss: true,
      dismissDelay: 3000,
      message: <span>Error: {error.message}</span>,
    });
  };

  const successMessage = (message) => {
    AlertService.alert({
      type: 'success',
      autoDismiss: true,
      dismissDelay: 3000,
      message: <span>{message} successfully updated.</span>,
    });
  };

  const saveCharity = async () => {
    try {
      if (!charityName) throw new Error('Nonprofit site name is required');
      const site = {
        siteName,
        charityName,
      };
      await updateSiteFields({
        variables: { site },
      });
      setCharities([...charities, { siteId: '', siteName: charityName }]);
      setCharityName('');
      successMessage('Charity');
    } catch (error) {
      errorMessage(error);
    }
  };

  const charitiesList = (
    <>
      <div className={classes.dataUtility}>
        <div className={classes.dataUtilityInner}>
          <h4>Charities</h4>
          <ul>
            {charities.map((charity) => (
              <li key={charity.siteName}>{charity.siteName}</li>
            ))}
          </ul>
          <h4>Add new nonprofit site</h4>
          <TextInput
            value={charityName}
            type="text"
            labelText="Nonprofit Site Name"
            onChange={setCharityName}
            required
          />
          <Button type="button" className={classes.button__prev} onClick={saveCharity}>
            Save
          </Button>
        </div>
      </div>
    </>
  );

  const handleSearchType = (value) => {
    if (!value?.result) return;
    setSiteName(value.result.siteName);
    setCharities(value.result.charities || []);
  };

  return (
    <div>
      <TypeAhead query={SiteNameTypeAheadQuery} queryName="siteNameTypeAheadQuery" onChange={handleSearchType} />
      <div className={classes.dataUtilitiesGrid}>
        {charities.length > 0 && charitiesList}
        <div className={classes.dataUtility}>
          <div className={classes.dataUtilityInner}>
            <DeletePostsWidget classes={classes} />
          </div>
        </div>
        <div className={classes.dataUtility}>
          <div className={classes.dataUtilityInner}>
            <h4>Update Average Gross Margin</h4>
            <TextInput
              value={siteId}
              onChange={(value) => setSiteId(value)}
              labelText="Site ID (DC not parent site)"
              required
              type="text"
            />
            <TextInput
              value={averageGrossMargin}
              onChange={(value) => setAverageGrossMargin(value)}
              labelText="Average Gross Margin"
              required
              type="number"
            />
            <DatePicker label="Date" onChange={handleDateSelection} day={day} id={'date-picker'} />
            <Button
              type="submit"
              disabled={grossMarginLoading}
              primary
              loading={grossMarginLoading}
              loadingText="Saving..."
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

HelloFreshAdminPage.propTypes = {
  user: PropTypes.object,
  history: PropTypes.object,
  mutate: PropTypes.func,
  match: PropTypes.object,
};

export default HelloFreshAdminPage;
